import { useState } from 'react';
import { pictureUrl, textureUrl } from '../api/catalog';

/**
 * The game's own picture of a class: a man, a vehicle, a weapon.
 *
 * Addressed by class when there is one, because that is the question the panel
 * already answers: it has been extracting weapon icons and vehicle previews out
 * of the same installation since August, caching them on the same disk, and
 * refusing the dedicated server's stripped 8x8 placeholders. Only when it cannot
 * does the platform fall back to the texture path the probe recorded and its own
 * readers. Nothing of anybody else's art is shipped either way: both roads end
 * at the copy the host already owns.
 *
 * A class with no picture, a mod whose path nothing can read, a catalogue
 * scanned before the probe learnt to read one: all of them render nothing at
 * all, which is what the pickers looked like before and is never worse than it.
 */
export function AssetPicture({
  picture,
  name,
  size = 40,
  assetClass,
  kind = 'vehicle',
  modSetHash,
  thumb = false,
}: {
  picture?: string | null;
  name?: string;
  size?: number;
  /**
   * The Arma class itself, which is the question the panel already answers.
   * Deliberately not called `className`: that word means a CSS class everywhere
   * else in a React tree, and one passed here by habit would become a request
   * for a picture of it.
   */
  assetClass?: string | null;
  kind?: 'weapon' | 'vehicle' | 'marker';
  modSetHash?: string | null;
  /**
   * A wide picture on top of a card rather than an icon beside a row: an editor
   * preview is a landscape shot of a vehicle, and a square box of it is mostly
   * background. `size` is still the hint the browser lays out with before the
   * picture arrives.
   */
  thumb?: boolean;
}) {
  const [broken, setBroken] = useState(false);
  if ((!assetClass && !picture) || broken) {
    return null;
  }
  return (
    <img
      className={thumb ? 'asset-picture thumb' : 'asset-picture'}
      src={
        assetClass
          ? pictureUrl(kind, assetClass, modSetHash ?? undefined)
          : textureUrl(picture as string)
      }
      width={size}
      height={size}
      alt=""
      title={name}
      aria-hidden="true"
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
}
